import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import './scss/campaignPage.scss';
import { logout, clearAnswers, fetchScratchTicket, getPosts, uploadTemporaryFile, getQuizResults, getRemainingPrizeCount, getElementGraph } from "./campaignActions";
import { genUUID } from "./utils/Misc.mjs";
import { loadCampaignFont } from "./utils/fontLoading.mjs";
import {showsCorrectAnswerEndPage, getCorrectAnswers, isTryAgainCampaign, isTryAgainPrize, showsPageCount, alwaysShowRestart, hideRestart, CAMPAIGN2_INFO_PAGE} from './utils/custom.mjs';

import FreeText from "./elements/FreeText"
import Image from "./elements/Image"
import Link from "./elements/Link"
import NumericPrediction from "./elements/NumericPrediction"
import RegistrationForm from "./elements/RegistrationForm"
import Spacer from "./elements/Spacer"
import SubmitButton from "./elements/SubmitButton"
import Text from "./elements/Text"
import Poll from "./elements/Poll"
import QuizResults from "./elements/QuizResults"
import ImageChoice from "./elements/ImageChoice"
import CategorizedPoll from "./elements/CategorizedPoll"
import WheelOfPrizes from "./elements/WheelOfPrizes"
import WheelOfPrizesMakis from "./elements/WheelOfPrizesMakis"
import WheelOfPrizesPreview from "./elements/WheelOfPrizesPreview"
import SoccerLineUpSelector from "./elements/SoccerLineUpSelector"
import SlideShow from "./elements/SlideShow"
import Mvp from "./elements/Mvp"
import Scratch from "./elements/Scratch"
import ShareModal from './ShareModal'
import CustomCorrectAnswers from './CustomCorrectAnswers'

import { hexToRGB, removeOpacityFromRGBA } from "./utils";
import IFrame from "./elements/IFrame";
import UGC from "./elements/UGC";
import TimerElement from "./elements/TimerElement";

import ReCAPTCHA from "react-google-recaptcha";

const getCurrentPageNum = (campaign) =>{
	const s = new Set();
	campaign.pages.forEach(p=>{
		p.elements.forEach(e=>{
			if(e.type==="multipleChoice") s.add(p.id);
		})
	})
	const o = JSON.parse(localStorage.getItem('_as')||'{}');
	let c = 1;
	for(const key in o){
		if(s.has(key)) c++;
	}
	return c;
}

function PollGraph({campaignId, element}) {
	const [graphData, setGraphData] = useState(null);
	const optionsMap = {};
	element.options.forEach((o)=>{
		optionsMap[o.id] = o;
	});
	const isLoading = graphData===null;

	useEffect(()=>{
		getElementGraph(campaignId, element.id)
		.then(dt=>{
			setGraphData(dt)
		})
	}, [campaignId, element.id]);

	return (<div className="poll poll__app">
		<div className="poll__header" dangerouslySetInnerHTML={{ __html: element.question }}></div>
		{element.options.map(o=>{
			const total = graphData?.total??0;
			const num = total?(graphData?.options?.[o.id]??0):0;
			const perc = total?(100*num/total):0;
			const roundedPerc = (Math.round(perc*100)/100).toFixed(1);
			return (
				<div key={o.id} className="poll__element" style={{marginBottom: 10}}>
					<div className="flex" style={{ justifyContent: 'center', alignItems: 'center',  gap: 20, marginBottom: 5 }}>
						<div style={{flex:1}}>
							<div>{o.text}</div>
						</div>
						<div className="font--20 text--center" style={{ width: 60}}>
							{roundedPerc}%
						</div>
						

					</div>
					<div className="flex" style={{ justifyContent: 'center', alignItems: 'center',  gap: 20 }}>
						<div style={{flex:1}}>
							<div style={{height:"12px", position: "relative", borderRadius: 10}}>
								<div style={{background:"var(--app-primary)", borderRadius: 10, height:"100%", position: "absolute", top:0, left:0, width: `${perc}%`}}></div>
							</div>
						</div>
						<div className="text--center">
							<div className="font--14 text--muted" style={{ width: 60}}>
								{num} votes
							</div>
						</div>
					</div>
				</div>
			);
		})}
	</div>)
}

function CampaignPage({ data, sending, elementsErrors, onPageSubmit, campaign, goToNextPage, result, campaignId, correctAnswer, hideSubmitButton, onStartTimer }) {
	const [campaignStyling, setCampaignStyling] = useState();
	const [userResponses, setUserResponses] = useState();
	const [remainingPrizes, setRemainingPrizes] = useState();
	const [currentPageNumber, setCurrentPageNumber] = useState(null);
	const [showingGraphs, setShowingGraphs] = useState(false);
	const {id, elements} = data;
	const {
		background,
		mobileBackground,
		tint,
		font,
		layout,
		primaryTextColor,
		secondaryTextColor,
		primaryColor,
		secondaryColor,
		showShare,
		multiVoting,
		hasCaptcha,
		restartText,
		canClear,
		clearText,
	} = campaign;

	const [captchaToken, setCaptchaToken] = useState(null);
	const recaptchaRef = useRef(null);

	const widthClass = layout?.width?`campaign__width__${layout.width}`:'';
	const sideSpacingClass = layout?.sideSpacing?`campaign__sideSpacing__${layout.sideSpacing}`:'';

	const isMakis = useMemo(() => {
		return ['Gw4zB0y84roJWZ5wMNMK', 'ulwSCV5B2nPFjlz69AGz', 'KaN9FoJx4Sv0nfTAvIYU'].includes(campaignId);
	}, [campaignId])
	const showPageCount = useMemo(() => {
		return showsPageCount(campaignId);
	}, [campaignId]);
	const currentPageNum = useMemo(() => {
		if(showPageCount) return getCurrentPageNum(campaign);
		return null;
	}, [data.id]);
	const forceShowRestart = useMemo(() => alwaysShowRestart(campaignId), [campaignId]);

	const showGraphAfterSubmit = elements.some(e=>e.graphAfterSubmit);

	useEffect(() => {
		if(!isTryAgainCampaign(campaignId)) return;
		const fetchData = async () => {
			getRemainingPrizeCount(campaignId)
			.then((dt)=>setRemainingPrizes(dt?.prizeCount))
			.catch(console.error)
		};

		fetchData();

		const intervalId = setInterval(fetchData, 5000);

		return () => {
			clearInterval(intervalId);
		};
	}, [campaignId]);

	useEffect(()=>{
		loadCampaignFont(font);
	}, [font]);

	console.log("~CampaignPage", id, userResponses)
	const setData = (id, dt) => {
		const _n = {...userResponses, [id]: dt}
		console.log('* CampaignPage sets', userResponses, '->', _n)
		console.log(_n);
		setUserResponses(_n)
	};
	function onSubmit (e, autoNext=true, willNotRetry=false){
		if(showGraphAfterSubmit){
			if(!showingGraphs) {
				autoNext = false;
				onPageSubmit(id, userResponses, autoNext, willNotRetry, captchaToken??undefined)
				.then(()=>setShowingGraphs(true))
			}else{
				goToNextPage();
			}
		}else{
			return onPageSubmit(id, userResponses, autoNext, willNotRetry, captchaToken??undefined);
		}
	}
	const body = document.querySelector('body');
	const captchaElementId = hasCaptcha?elements.find((elementData)=>{
		return elementData.type==="submitButton" || elementData.type==="registrationForm";
	})?.id:null;

	const calcCampaignStyling = () => {
		let d = null;
		if(background && mobileBackground){
			const b = body.getBoundingClientRect();
			d = b.width > 600 ? background : mobileBackground;
		}else{
			d = background ? background : mobileBackground;
		}
		if(!d) return;

		const backgroundPosition = d.position ? d.position.x+" "+d.position.y : "center";
		const backgroundRepeat = d.repeat ? d.repeat : 'no-repeat';
		const backgroundSize = d.size ? d.size : 'contain';
		const backgroundImage = d.url ? "url("+d.url+")" : '';
		setCampaignStyling({
			"fontFamily": font?.family,
			"fontSize": font?.size,
			"backgroundPosition": backgroundPosition,
			"backgroundRepeat": backgroundRepeat,
			"backgroundSize": backgroundSize,
			"backgroundImage": backgroundImage,
			"--campaign-background-position": backgroundPosition,
			"--campaign-background-repeat": backgroundRepeat,
			"--campaign-background-size": backgroundSize,
			"--campaign-background-image": backgroundImage,
		});
	}
	const getTintStyling = () => {
		if(!tint) return;
		const background = tint.background ? tint.background : "";
		return {
			"background": background,
			"--tint-background": background,
		}
	}
	let resizeObserver = null;
	try {
		resizeObserver = new ResizeObserver((entries) => {
			console.log('Observer observe...');
			calcCampaignStyling();
		});
	} catch (err) {
		console.error('Observer error:', err);
	}
	const getCampaignShareUrl = () => {
		return (window.location !== window.parent.location) ? document.referrer : 'https://app.sportovivo.com/share?campaign=' + window.location.pathname.substring(1);
	}
	const isFirstPage = !!data.id && campaign.pages[0]?.id===data.id;
	const islastPage = !!data.id && campaign.pages[campaign.pages.length - 1]?.id===data.id;
	const hasRestart = () => {
		return multiVoting && (islastPage||(forceShowRestart&&!isFirstPage)) && !hideRestart(campaignId);
	}
	const hasClear = () => {
		return canClear && islastPage;
	}
	const showCorrect = showsCorrectAnswerEndPage(campaignId);
	useEffect(() => {
		if(resizeObserver) resizeObserver.observe(body);
		const root = document.querySelector(':root');
		if(root){
			if(primaryColor){
				root.style.setProperty('--app-primary', primaryColor);
				root.style.setProperty('--app-primary-rgb', hexToRGB(primaryColor));
			}
			if(primaryTextColor){
				root.style.setProperty('--app-textColor', primaryTextColor);
				root.style.setProperty('--app-textColor-rgb', hexToRGB(primaryTextColor));
			}
			if(secondaryColor){
				root.style.setProperty('--app-secondary', secondaryColor);
				root.style.setProperty('--app-secondary-rgb', removeOpacityFromRGBA(secondaryColor));
			}
			if (secondaryTextColor) {
				root.style.setProperty('--app-secondaryTextColor', secondaryTextColor);
				root.style.setProperty('--app-secondaryTextColor-rgb', hexToRGB(secondaryTextColor));
			}
		}

		// if(islastPage && multiVoting){
		// 	if(!showCorrect) setTimeout(() => {
		// 		logout();
		// 	}, 5000)
		// }
	}, [primaryColor, primaryTextColor, secondaryColor, secondaryTextColor])
	useEffect(()=>{
		if(data.isPartial) onStartTimer(id);
	}, []);
	return (
		<div className="campaign" style={campaignStyling}>
			<div className="campaign__tint" style={getTintStyling()}>
				<div className={`campaign__wrap ${widthClass} ${sideSpacingClass}`}>
					{elements.map(function(elementData) {
						const showCaptcha = isFirstPage && captchaElementId && elementData.id === captchaElementId;
						switch(elementData.type){
							case 'freeText':
								return <FreeText key={elementData.id} data={elementData} sending={sending} elementErrors={elementsErrors[elementData.id]} onChange={setData}/>;
							case 'image':
								return <Image key={elementData.id} data={elementData}/>;
							case 'link':
								return <Link key={elementData.id} data={elementData}/>;
							case 'iframe':
								return <IFrame key={elementData.id} data={elementData}/>;
							case 'numericPrediction':
								return <NumericPrediction key={elementData.id} data={elementData} value={userResponses?.[elementData.id]} sending={sending} elementErrors={elementsErrors[elementData.id]} onChange={setData}/>;
							case 'registrationForm':
								return <RegistrationForm
									autofill={!multiVoting}
									key={elementData.id}
									data={elementData}
									value={userResponses?.[elementData.id]}
									sending={sending}
									elementErrors={elementsErrors[elementData.id]}
									button={campaign.buttons}
									onChange={setData}
									onSubmit={onSubmit}
									setCaptchaToken={showCaptcha?setCaptchaToken:null}
								/>;
							case 'spacer':
								return <Spacer key={elementData.id} data={elementData}/>;
							case 'submitButton':
								return <Fragment key={elementData.id}>
									{ id===CAMPAIGN2_INFO_PAGE && <CustomCorrectAnswers pages={campaign.pages} correctAnswers={getCorrectAnswers(campaignId)}/> }
									{ showCaptcha && <div className="ReCAPTCHA-container">
										<ReCAPTCHA
											ref={recaptchaRef}
											sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
											onChange={(token) => setCaptchaToken(token)}
											onExpired={() => setCaptchaToken(null)}
										/>
									</div>}
									{!hideSubmitButton && <SubmitButton
										key={elementData.id}
										text={elementData.text}
										size={elementData.width==="auto"?campaign.buttons?.submitSize:elementData.width}
										sending={sending}
										onSubmit={onSubmit}
										containerStyle={{marginTop:elementData.marginTop, textAlign:elementData.textAlign}}
										buttonStyle={{background:elementData.background, color:elementData.textColor, fontSize:elementData.fontSize, borderRadius:campaign.buttons?.borderRadius}}
									/>}
								</Fragment>;
							case 'text':
								return <Text key={elementData.id} data={elementData}/>;
							case 'poll':
							case 'multipleChoice':{
								const pageIsShowingGraphs = showGraphAfterSubmit && showingGraphs;
								const showGraph = pageIsShowingGraphs && elementData.graphAfterSubmit;
								const showPoll = !pageIsShowingGraphs;
								return <div key={elementData.id}>
									{showPageCount && <div style={{position: "fixed", top: 0, left: 0,width: "100%",height: "10px",overflow: "visible"}}>
										<div class="campaign__wrap" style={{textAlign: "right", paddingTop: "10px"}}>
											<span style={{background: "rgba(255,255,255,0.2)", padding: "5px", borderRadius: "5px"}}>{currentPageNum} / 10</span>
										</div>
									</div>}
									{showGraph && <PollGraph campaignId={campaignId} element={elementData} />}
									{showPoll && <Poll data={elementData} value={userResponses?.[elementData.id]} sending={sending} elementErrors={elementsErrors[elementData.id]} onChange={setData} correctAnswer={correctAnswer}/>}
								</div>;
							}
							case 'quizResults':
								return <QuizResults key={elementData.id} data={elementData} getQuizResults={()=>getQuizResults(campaignId, id)} campaignId={campaignId}/>;
							case 'imageChoice':
								return <ImageChoice key={elementData.id} data={elementData} value={userResponses?.[elementData.id]} sending={sending} elementErrors={elementsErrors[elementData.id]} onChange={setData} />;
							case 'categorizedPoll':
								return <CategorizedPoll key={elementData.id} data={elementData} value={userResponses?.[elementData.id]} sending={sending} elementErrors={elementsErrors[elementData.id]} onChange={setData} />;
							case 'wheelOfPrizes':
								if(isMakis){
									return <WheelOfPrizesMakis
										key={elementData.id}
										data={elementData}
										value={userResponses?.[elementData.id]}
										sending={sending}
										elementErrors={elementsErrors[elementData.id]}
										onChange={setData}
										onSubmit={onSubmit}
										goToNextPage={goToNextPage}
										result={result}
										primaryTextColor={primaryTextColor}
										button={campaign.buttons}
									/>;
								}
								else{
									return <WheelOfPrizes
										key={elementData.id}
										data={elementData}
										value={userResponses?.[elementData.id]}
										sending={sending}
										elementErrors={elementsErrors[elementData.id]}
										onChange={setData}
										onSubmit={onSubmit}
										goToNextPage={goToNextPage}
										result={result}
										primaryTextColor={primaryTextColor}
										button={campaign.buttons}
										logout={logout}
										isTryAgainPrize={isTryAgainPrize}
									/>;
								}
							case 'wheelOfPrizesPreview':
								return <WheelOfPrizesPreview
									key={elementData.id}
									data={elementData}
									campaign={campaign}
									value={userResponses?.[elementData.id]}
									sending={sending}
									elementErrors={elementsErrors[elementData.id]}
									onChange={setData}
									onSubmit={onSubmit}
									goToNextPage={goToNextPage}
									result={result?.responses?.[id]||null}
									primaryTextColor={primaryTextColor}
									button={campaign.buttons}
								/>;
							case 'soccerLineupSelector':
								return <SoccerLineUpSelector key={elementData.id} data={elementData} value={userResponses?.[elementData.id]} sending={sending} elementErrors={elementsErrors[elementData.id]} onChange={setData} />;
							case 'slideShow':
								return <SlideShow key={elementData.id} data={elementData}/>
							case 'mvp':
								return <Mvp key={elementData.id} data={elementData} value={userResponses?.[elementData.id]} sending={sending} elementErrors={elementsErrors[elementData.id]} onChange={setData} />;
							case 'scratch':
								return <Scratch
									key={elementData.id}
									data={elementData}
									value={userResponses?.[elementData.id]}
									sending={sending}
									elementErrors={elementsErrors[elementData.id]}
									onChange={setData}
									onSubmit={onSubmit}
									goToNextPage={goToNextPage}
									fetchTicket={fetchScratchTicket}
									result={result}
									primaryTextColor={primaryTextColor}
									campaignId={campaignId}
									button={campaign.buttons}
								/>;
							case 'ugc':
								return <UGC
									key={elementData.id}
									data={elementData}
									value={userResponses?.[elementData.id]}
									onChange={setData}
									onSubmit={onSubmit}
									fetchPosts={getPosts}
									uploadTemporaryFile={uploadTemporaryFile}
									genUUID={genUUID}
									campaignId={campaignId}
									button={campaign.buttons}
								/>
							case 'timer':
								return <TimerElement
									key={elementData.id}
									data={elementData}
									onTimeout={(e)=>onSubmit(e, true, true)}
								/>
							default:
								console.error('Invalid type', elementData.type);
								return <></>;
						}
					})}
					{ showShare && <ShareModal isStatic={true} campaign_url={getCampaignShareUrl()} />}
					{ hasRestart() &&
						<div className="campaign__revote">
							{isMakis &&
								<div className=""
									style={{
										cursor: 'pointer',
										width: '150px',
										margin: 'auto',
										backgroundColor: 'var(--app-primary)',
										color: 'var(--app-secondaryTextColor)',
										padding: '.65rem 3rem',
										borderRadius: campaign.buttons?.borderRadius || '3px'
								}} onClick={logout}>
									<span className="material-icons">refresh</span>
									{(restartText===null||restartText===undefined)?"Restart":restartText}
								</div>
							}
							{!isMakis &&
								<div className="btn btn-secondary btn-rounded" style={{ width: '150px', margin: 'auto' }} onClick={logout}>
									<span className="material-icons">refresh</span>
									{(restartText===null||restartText===undefined)?"Restart":restartText}
								</div>
							}
						</div>
					}
					{ hasClear() &&
						<div className="campaign__revote">
							<div className="btn btn-secondary btn-rounded" style={{ width: '150px', margin: 'auto' }} onClick={()=>clearAnswers(campaignId).then(()=>window.location.reload())}>
								<span className="material-icons">refresh</span>
								{(clearText===null||clearText===undefined)?"Clear":clearText}
							</div>
						</div>
					}
					{isTryAgainCampaign(campaignId) && <div className="prizeCount">{remainingPrizes}</div>}
				</div>
			</div>
		</div>
	);
}

export default CampaignPage;
